import { PartialMessage } from '@bufbuild/protobuf';
import {
    CollaborativeStudentGroupInput,
    CollaborativeStudentGroupResult,
    SectionStudentInfo,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { IProjectStudentGroup } from '../CreateProject/reducer/projectFlow.Slice';

/**
 * Collaboration groups for a Project assignment.
 *
 * The server validates every rule below before writing a single row, so a bad
 * payload rejects the whole assignment. We mirror the checks here so the
 * teacher is stopped at the field they got wrong instead of at the end of a
 * long form - the server messages are still shown verbatim if anything slips
 * through, since they are written for humans.
 */

// Must match BE assessment.utils.ts (MAX_COLLABORATIVE_GROUPS_PER_ASSESSMENT,
// MAX_STUDENTS_PER_COLLABORATIVE_GROUP) or the whole assignment is rejected.
export const MAX_GROUPS_PER_ASSESSMENT = 10;
export const MAX_STUDENTS_PER_GROUP = 10;
export const MAX_GROUP_NAME_LENGTH = 100;
/** Reserved by the section-wide group the server keeps for every assignment. */
export const RESERVED_GROUP_NAME = 'studentGroup';

/** Local correlation id. Never persisted - the server matches groups by name. */
export const createClientGroupId = () =>
    `g-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const getGroupedStudentIds = (groups: IProjectStudentGroup[]) =>
    new Set(groups.flatMap((group) => group.studentIds));

/** Students the assignment covers who are not in any group yet. */
export const getUngroupedStudents = (
    groups: IProjectStudentGroup[],
    assignedStudents: SectionStudentInfo[]
) => {
    const grouped = getGroupedStudentIds(groups);
    return assignedStudents.filter(
        (student) => !grouped.has(student.studentId.toString())
    );
};

/** Students already taken by a group other than `exceptClientGroupId`. */
export const getStudentIdsTakenByOtherGroups = (
    groups: IProjectStudentGroup[],
    exceptClientGroupId?: string
) =>
    new Set(
        groups
            .filter((group) => group.clientGroupId !== exceptClientGroupId)
            .flatMap((group) => group.studentIds)
    );

interface IValidateProps {
    groups: IProjectStudentGroup[];
    /** The sections this assignment covers. */
    sectionIds: bigint[];
}

/**
 * Returns the first problem as a teacher-facing message, or undefined when the
 * groups are safe to submit. Order follows the server's own validation order.
 */
export const validateProjectGroups = (
    props: IValidateProps
): string | undefined => {
    const { groups, sectionIds } = props;

    // No groups at all is valid - that is the ordinary one-submission-per-student
    // assignment, and studentGroups is simply not sent.
    if (groups.length === 0) return undefined;

    if (groups.length > MAX_GROUPS_PER_ASSESSMENT) {
        return `A maximum of ${MAX_GROUPS_PER_ASSESSMENT} groups can be created for one assessment.`;
    }

    const seenNames = new Set<string>();
    const studentToGroup = new Map<string, string>();

    for (const group of groups) {
        const name = group.groupName.trim();

        if (!name) return 'Every group needs a name.';

        if (name.length > MAX_GROUP_NAME_LENGTH) {
            return `Group name "${name}" is longer than ${MAX_GROUP_NAME_LENGTH} characters.`;
        }

        if (name.toLowerCase() === RESERVED_GROUP_NAME.toLowerCase()) {
            return `"${RESERVED_GROUP_NAME}" is a reserved group name.`;
        }

        if (seenNames.has(name.toLowerCase())) {
            return `Duplicate group name: ${name}.`;
        }
        seenNames.add(name.toLowerCase());

        if (!sectionIds.includes(group.sectionId)) {
            return `Group "${name}" refers to a section that is not part of this assignment.`;
        }

        if (group.studentIds.length === 0) {
            return `Group "${name}" has no students.`;
        }

        if (group.studentIds.length > MAX_STUDENTS_PER_GROUP) {
            return `Group "${name}" has more than ${MAX_STUDENTS_PER_GROUP} students.`;
        }

        const seenInThisGroup = new Set<string>();
        for (const studentId of group.studentIds) {
            if (seenInThisGroup.has(studentId)) {
                return `A student is listed twice in group "${name}".`;
            }
            seenInThisGroup.add(studentId);

            const otherGroup = studentToGroup.get(studentId);
            if (otherGroup) {
                return `A student is in both "${otherGroup}" and "${name}". A student can belong to only one group.`;
            }
            studentToGroup.set(studentId, name);
        }
    }

    // Full coverage is deliberately NOT required: students left out of every
    // group simply do not get the project.
    return undefined;
};

/** Groups -> the request field. Student ids go back to int64 here. */
export const toCollaborativeGroupInputs = (
    groups: IProjectStudentGroup[]
): PartialMessage<CollaborativeStudentGroupInput>[] =>
    groups.map((group) => ({
        clientGroupId: group.clientGroupId,
        groupName: group.groupName.trim(),
        sectionId: group.sectionId,
        studentIds: group.studentIds.map((id) => BigInt(id)),
    }));

/**
 * Merge the server's ids back onto the local groups, joined on clientGroupId.
 * The response carries every section's groups flattened into one array.
 */
export const mergeGroupResults = (
    groups: IProjectStudentGroup[],
    results: CollaborativeStudentGroupResult[]
): IProjectStudentGroup[] =>
    groups.map((group) => {
        const result = results.find(
            (r) => r.clientGroupId === group.clientGroupId
        );
        return result
            ? {
                ...group,
                studentGroupId: result.studentGroupId,
                groupName: result.groupName,
                studentIds: result.studentIds.map((id) => id.toString()),
            }
            : group;
    });
